/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect, useState } from 'react';
import { Button, Icon, FileDrop } from '@scuf/common';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { useFileUploadContext } from '../../../../context/FileUploadContext/FileUploadContext';
import FileUploadAction from '../../../../context/FileUploadContext/FileUploadActions';
import {
  fileUploadSizeDecimal,
  MAGIC_VALUE,
  UpdatedManualFileUploadTypeList,
  UpdatedTemplateDownloadList
} from '../../../shared/Utils/Constants';
import { formatBytes, isCsvInvalid } from '../../../shared/Utils/Utils';
import '../../MeterDataUploadModal/ChooseDataTypeSection/UpdatedChooseDataTypeThemes.scss';
import {
  IAny,
  IChunkedUploadProgressState
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { ApiContext } from '../../../../context/ApiContext';

export const uploadTypeCheck = (SelectedDataTye: string) =>
  UpdatedManualFileUploadTypeList.find((r) => r.type === SelectedDataTye)
    ?.allowedFiles[0]?.uploadType || '';

export const meterDataminSizeCheck = (SelectedDataTye: string) =>
  UpdatedManualFileUploadTypeList.find((r) => r.type === SelectedDataTye)
    ?.allowedFiles[0]?.minSize || 0;

export const meterDatamaxSizeCheck = (SelectedDataTye: string) =>
  UpdatedManualFileUploadTypeList.find((r) => r.type === SelectedDataTye)
    ?.allowedFiles[0]?.maxSize || 0;

const parseFileSize = (size: number | undefined) => {
  if (!size) return 0;
  return size;
};

const getFileAcceptedTypesforCSV = () => '.csv';

function UpdatedUploadTab(props: {
  selectedDataType: string;
  setUploadDisabled?: IAny;
  setUploadDisableonFileError: Function;
  dataError?: IAny;
}) {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const [state, dispatch] = useFileUploadContext();
  let acceptedFiles: File[] = [],
    csvHeaders: string[] = [];
  acceptedFiles = state ? state.acceptedFiles : [];
  csvHeaders = state ? state.acceptedCsvHeaders : [];
  const apiContext = useContext(ApiContext);
  const { onNewTemplateDownloadClick, progressTrackingList } = apiContext;
  const { selectedDataType } = props;
  const [totalSize, setTotalSize] = useState(0);
  const [numSelectedFiles, setNumSelectedFiles] = useState(0);
  const maxFileSize = meterDatamaxSizeCheck(selectedDataType);
  const [invalidFiles, setInvalidFiles] = useState([] as File[]);
  const [validHeaders, setValidHeaders] = useState<Boolean>(true);
  const [acceptedFile, setAcceptedFile] = useState<File[]>([]);
  const [fileCountError, setFileCountError] = useState<string | null>(null);
  const [fileSizeError, setFileSizeError] = useState<string | null>(null);

  const updateFiles = (
    action: 'add' | 'remove',
    file?: File,
    progressList?: IChunkedUploadProgressState[]
  ) => {
    let newSize = totalSize;
    let newNumFiles = numSelectedFiles;
    let updatedAcceptedFiles: File[] = acceptedFiles;

    if (action === 'add' && file) {
      //reset when add
      updatedAcceptedFiles = [];
      newSize += file.size;
      // newNumFiles++;
      if (progressList)
        newNumFiles =
          1 + progressList?.filter((ul) => ul.removed !== true).length; //- progressList?.filter(ul=>ul.removed === true).length

      if (newNumFiles > 10 || newSize > maxFileSize) {
        setFileCountError(newNumFiles > 10 ? t(d.NUMBEROFFILEALLOWED) : null);
        setFileSizeError(
          newSize > maxFileSize ? t(d.TOTALSIZEEXCEEDSERROR) : null
        );

        setTimeout(() => {
          setFileCountError(null);
          setFileSizeError(null);
        }, 5000);
      } else {
        updatedAcceptedFiles.push(file);
        setTotalSize(newSize);
        setNumSelectedFiles(newNumFiles);
        setAcceptedFile(updatedAcceptedFiles);
        dispatch(FileUploadAction.setAcceptedFile(updatedAcceptedFiles));
      }
    } else if (action === 'remove' && file) {
      // const fileIndex = updatedAcceptedFiles.findIndex(existingFile => existingFile.name === file.name);

      // if (fileIndex !== -1) {
      newSize -= updatedAcceptedFiles[0].size;
      newNumFiles--;
      // updatedAcceptedFiles.splice(fileIndex, 1);
      setTotalSize(newSize);
      setNumSelectedFiles(newNumFiles);
      setAcceptedFile([]);
      dispatch(FileUploadAction.setAcceptedFile([]));
      // }
    }
  };

  useEffect(() => {
    setInvalidFiles([]);
    acceptedFiles.forEach((file) => {
      isCsvInvalid(file, csvHeaders).then((isInvalid) => {
        addToInvalidFiles(isInvalid, file);
      });
      // validateLargeCSVFileHeaders(file, allowedHeaders)
      //   .then((isValid) => setValidHeaders(isValid))
      //   .catch((err) => {
      //     console.error(err);
      //     setValidHeaders(false);
      //   });
    });
  }, [acceptedFiles]);

  const showFileDataError = (dataError: IAny) => {
    return (
      dataError &&
      dataError.length > 0 &&
      dataError.map((err: IAny) => {
        return <div className="meter-data-upload-err-msg">{err.message}</div>;
      })
    );
  };

  const addToInvalidFiles = (isInvalid: boolean, file: File) => {
    if (
      isInvalid &&
      file.size <= parseFileSize(allowedFiles?.maxSize) &&
      file.size > parseFileSize(allowedFiles?.minSize)
    ) {
      setInvalidFiles([...invalidFiles, file]);
    }
  };

  const allowedFiles = UpdatedManualFileUploadTypeList.find(
    (r) => r.type === selectedDataType
  )?.allowedFiles[0];

  const allowedHeaders = allowedFiles?.headers;

  const selectedTemplateObject = UpdatedTemplateDownloadList.find(
    (x) => x.type === selectedDataType
  );
  return (
    <div className="Updated-File-Upload-for-second-partition">
      <div
        className={
          acceptedFiles.length === 0
            ? 'meter-data-file-drop'
            : 'updated-meter-data-file-drop'
        }
      >
        <div className="ptb-10 flex">
          <Icon root="common" name="file-download" size="medium" />
          <div className="plr-5">
            <Button
              type="link"
              className="download-csv-button-updated"
              content={t(d.DOWNLOAD_METER_DATA_CSV_TEMPLATE)}
              onClick={() => {
                if (onNewTemplateDownloadClick)
                  // Download blank template
                  onNewTemplateDownloadClick(
                    selectedDataType,
                    selectedTemplateObject?.templateFiles
                      .PointHistoryReferenceSchema.downloadFile || '',
                    selectedTemplateObject?.templateFiles
                      .PointHistoryReferenceSchema.uploadType || ''
                  );
              }}
            />
          </div>
        </div>
        <FileDrop
          className="ptb-10"
          acceptedTypes={getFileAcceptedTypesforCSV()}
          placeholder={t(d.SINGLEFILEUPLOADPLACEHOLDER)}
          error={t(d.UPDATEDUPLOADNOTE, {
            type: allowedFiles?.type,
            size: allowedFiles
              ? allowedFiles?.maxSize / MAGIC_VALUE.MEGABYTES
              : MAGIC_VALUE.FIFTY
          })}
          onAccepted={(files: File[]) =>
            updateFiles('add', files[0], progressTrackingList)
          }
        />
        <div>
          {fileCountError && (
            <div style={{ color: 'red' }}>{fileCountError}</div>
          )}
          {fileSizeError && <div style={{ color: 'red' }}>{fileSizeError}</div>}
        </div>
      </div>
      {acceptedFiles && acceptedFiles.length > 0 && (
        <div className="meter-data-file-count">
          {acceptedFiles.length} {t(d.FILE)}:
        </div>
      )}
      <div className="meter-data-file-name">
        {acceptedFiles &&
          acceptedFiles.map((file: File, key: number) => {
            if (
              selectedDataType &&
              (file.size <= parseFileSize(allowedFiles?.minSize) ||
                file.size >= parseFileSize(allowedFiles?.maxSize) ||
                !validHeaders)
            ) {
              props.setUploadDisabled(true);
            }
            return (
              <div className="uploaded-meter-data" key={`uploadedfile-${key}`}>
                <div className="full-width uploaded-file-details">
                  <div className="w-6">
                    <Icon root="common" name="document-report" size="small" />
                  </div>
                  <div className="w-79">
                    <div>
                      {file.name} (
                      {formatBytes(file.size, fileUploadSizeDecimal)})
                    </div>
                    <div>
                      {selectedDataType &&
                        file.size <= parseFileSize(allowedFiles?.minSize) && (
                          <div className="meter-data-upload-err-msg">
                            {t(d.FILEEMPTYERRORMSG)}
                          </div>
                        )}
                      {selectedDataType &&
                        file.size > parseFileSize(allowedFiles?.maxSize) && (
                          <div className="meter-data-upload-err-msg">
                            {t(d.FILEMAXSIZEERRORMSG, {
                              maxSize: allowedFiles
                                ? allowedFiles?.maxSize / MAGIC_VALUE.MEGABYTES
                                : '1.5GB'
                            })}
                          </div>
                        )}
                      {selectedDataType && !validHeaders && (
                        <div className="meter-data-upload-err-msg">
                          {t(d.FILEHEADERERROR)}
                        </div>
                      )}
                      {invalidFiles.includes(file) && (
                        <div className="meter-data-upload-err-msg">
                          {t(d.CSVFILEINVALID)}
                        </div>
                      )}
                      {props.dataError &&
                        props.dataError.length > 0 &&
                        showFileDataError(props.dataError)}
                    </div>
                  </div>
                  <div className="meter-data-remove-button w-15 pl-5">
                    <Button
                      type="link"
                      content={t(d.REMOVE)}
                      onClick={() => updateFiles('remove', file)}
                    />
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}

export default UpdatedUploadTab;
